import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function CompanyLogoUploadDialog({
  open,
  logoSrc,
  companyName,
  isUploading,
  uploadError,
  uploadSuccess,
  onClose,
  onFileChange,
  onSave,
  canSave,
  onClear,
  canClear = true,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            bgcolor: isDark
              ? theme.palette.background.default
              : theme.palette.background.paper,
            // border: "1px solid",
            // borderColor: isDark ? "divider" : "transparent",
            boxShadow: isDark
              ? "0 20px 45px rgba(0,0,0,0.6)"
              : theme.shadows[12],
          },
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.3rem", my: 0, py: 2 }}>
        Upload Company Logo
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5} alignItems="center">
          {!logoSrc && (
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "primary.main",
                borderRadius: 2,
                width: 200,
                height: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background 0.2s",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
              onClick={() => {
                // Trigger the hidden file input click
                document.getElementById("clu-fileupload")?.click();
              }}
              role="button"
              tabIndex={0}
            >
              <Typography variant="button" color="primary" fontWeight="bold">
                Upload a Logo
              </Typography>
              <input
                id="clu-fileupload"
                name="logo-upload"
                type="file"
                accept="image/png, image/jpeg"
                style={{ display: "none" }}
                onChange={onFileChange}
              />
            </Box>
          )}
          {logoSrc && (
            <Box
              sx={{
                width: 200,
                height: 120,
                border: "3px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <img
                src={logoSrc}
                alt={companyName || "Company Logo"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" align="center">
            Supported formats: JPG, PNG. Max file size 1 MB.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            {logoSrc && (
              <Button
                variant="contained"
                color="primary"
                onClick={onClear}
                disabled={!canClear || isUploading}
                fullWidth
              >
                Change Logo
              </Button>
            )}
          </Stack>
          {uploadError && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {uploadError}
            </Alert>
          )}
          {uploadSuccess && (
            <Alert severity="success" sx={{ width: "100%" }}>
              Logo updated successfully.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={!canSave || isUploading}
        >
          {isUploading ? "Saving..." : "Save logo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
